//
//  HelpTourStepsField.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the tour step text editor of the help topic form.
//

import {
    Accordion,
    Badge,
    Box,
    Button,
    Code,
    Group,
    Paper,
    Stack,
    Switch,
    Text,
    Title
} from "@mantine/core";
import {
    Collection,
    Scope,
    useArrayFieldActions,
    useArrayFieldItemIndex,
    useFieldActions,
    useFieldDisabled,
    useFieldSelector,
    useFieldSnapshot,
    useStableCallback,
    type ArrayFieldActions
} from "@ngt-form/core";
import { GroupedFieldsProvider, TextInput } from "@ngt-form/mantine";
import { memo, useMemo, type FC } from "react";

import type { HelpTopic, HelpTourStep as StoredTourStep, LookupOption } from "src/api/Tmd.dtos";
import LongGroupedField from "src/components/forms/fields/grouped/LongGroupedField";
import RichTextEditorInput from "src/components/forms/inputs/RichTextEditorInput";
import type { TMDFormSubmitMetadata } from "src/components/forms/TMDFormSubmitButtons";
import helpTours, { type HelpTourStep } from "src/help/helpTours";
import {
    defaultTourStepContent,
    defaultTourStepTitle,
    isTourStepChanged,
    tourStepKey
} from "src/help/tourStepText";
import type { TMDValidationError } from "src/types/TMDValidationError";

type TourStepActions = ArrayFieldActions<
    HelpTopic,
    TMDValidationError,
    TMDFormSubmitMetadata,
    StoredTourStep,
    LookupOption
>;

/** The chapter heading for steps that belong to no chapter, such as the closing help step. */
const closingChapter = "Closing";

/** Options for step actions, which change the draft only. */
const changeOptions = { notify: true, updateDirty: true };

/** Identifies stored rows by step key, so a row keeps its editor when other rows change. */
const storedStepKey = (row: StoredTourStep | null | undefined, index: number) =>
    row?.stepKey ?? index;

/** Builds a stored row for a step from its default wording. */
const defaultStoredStep = (step: HelpTourStep, active: boolean) =>
    ({
        stepKey: tourStepKey(step),
        title: defaultTourStepTitle(step),
        content: defaultTourStepContent(step),
        active
    }) as StoredTourStep;

/** Describes a step's default wording for its preview. */
const defaultPreview = (step: HelpTourStep) =>
    typeof step.content === "string"
        ? step.content
        : "This step's default text is generated from the page.";

/** Reads a field of the collection's current row. */
const useRowValue = <TValue,>(propertyPath: keyof StoredTourStep) =>
    useFieldSnapshot<HelpTopic, TMDValidationError, TMDFormSubmitMetadata, TValue>(
        { value: true },
        { scope: Scope.PropertyPath, propertyPath, prependParentPropertyPath: true }
    ).value;

/** Sets a field of the collection's current row. */
const useSetRowValue = <TValue,>(propertyPath: keyof StoredTourStep) =>
    useFieldActions<HelpTopic, TMDValidationError, TMDFormSubmitMetadata, TValue, LookupOption>({
        scope: Scope.PropertyPath,
        propertyPath,
        prependParentPropertyPath: true
    }).setValue;

/** Supplies the hard-coded step whose stored row is rendered. */
type TourStepPartProps = {
    step: HelpTourStep;
};

/**
 * Renders the step's heading: its entered title, or its default title when none is entered.
 *
 * Preconditions: Rendered within a tour steps collection row.
 * Postconditions: Subscribes to the row's title alone, so typing a title renders only the heading.
 */
const TourStepHeading: FC<TourStepPartProps> = ({ step }) => {
    const title = useRowValue<string>("title");

    return <Text fw={600}>{title?.trim() || defaultTourStepTitle(step) || tourStepKey(step)}</Text>;
};

/**
 * Renders the Changed badge and Use default action while the row's text differs from its default.
 *
 * Preconditions: Rendered within a tour steps collection row.
 * Postconditions: Subscribes to whether the row differs from the default, not to its text, so it
 * renders only when that changes. Use default returns the title and text to the default wording in
 * the draft.
 */
const TourStepDefaultControls: FC<TourStepPartProps> = ({ step }) => {
    const disabled = useFieldDisabled();
    const isChanged = useStableCallback(
        (snapshot: { value?: StoredTourStep | null }) =>
            !!snapshot.value && isTourStepChanged(step, snapshot.value)
    );
    const changed = useFieldSelector<
        HelpTopic,
        TMDValidationError,
        TMDFormSubmitMetadata,
        StoredTourStep,
        LookupOption,
        boolean
    >(isChanged, { value: true }, { scope: Scope.Context });
    const setTitle = useSetRowValue<string>("title");
    const setContent = useSetRowValue<string>("content");

    if (!changed) {
        return null;
    }

    return (
        <>
            <Badge color="blue">Changed</Badge>
            <Button
                size="xs"
                variant="default"
                disabled={disabled}
                onClick={() => {
                    setTitle(defaultTourStepTitle(step) ?? "", changeOptions);
                    setContent(defaultTourStepContent(step), changeOptions);
                }}
            >
                Use default
            </Button>
        </>
    );
};

/** Supplies the page tour's steps by key. */
type HelpTourStepItemProps = {
    stepsByKey: Map<string, HelpTourStep>;
};

/**
 * Renders one stored tour step with its title, text and tour visibility.
 *
 * Preconditions: Rendered by a tour steps collection, which scopes property paths to the row.
 * Postconditions: The card subscribes only to the row's key and active flag. The heading, the
 * changed controls and each field subscribe to their own values, so typing renders only the part
 * that shows what was typed, and no other step. All actions update the draft only.
 */
const HelpTourStepItem: FC<HelpTourStepItemProps> = memo(({ stepsByKey }) => {
    const disabled = useFieldDisabled();
    const key = useRowValue<string>("stepKey");
    const active = useRowValue<boolean>("active") !== false;
    const setActive = useSetRowValue<boolean>("active");
    const step = stepsByKey.get(key);

    if (!step) {
        return null;
    }

    return (
        <Paper
            withBorder
            p={0}
            mb="sm"
            opacity={active ? 1 : 0.6}
        >
            <Group
                justify="space-between"
                wrap="nowrap"
                p="sm"
                gap="sm"
            >
                <Box style={{ minWidth: 0 }}>
                    <TourStepHeading step={step} />
                    <Code>{key}</Code>
                </Box>
                <Group
                    gap="xs"
                    wrap="nowrap"
                >
                    <TourStepDefaultControls step={step} />
                    <Switch
                        label="Show in tour"
                        labelPosition="left"
                        size="xs"
                        checked={active}
                        disabled={disabled}
                        onChange={event => setActive(event.currentTarget.checked, changeOptions)}
                    />
                </Group>
            </Group>
            <LongGroupedField
                propertyPath="title"
                formInputComponent={TextInput}
                placeholder={defaultTourStepTitle(step)}
                description="Leave empty to keep the default title."
            />
            <LongGroupedField
                propertyPath="content"
                formInputComponent={RichTextEditorInput}
            />
        </Paper>
    );
});

/** Supplies a step without a stored row and the actions used to add one. */
type UnstoredTourStepItemProps = {
    step: HelpTourStep;
    collectionActions: TourStepActions;
};

/**
 * Renders a step that has no stored row, with its default wording.
 *
 * Postconditions: Adding text, or hiding the step, adds a row from the default wording to the
 * draft; the step is then edited through the chapter's collection.
 */
const UnstoredTourStepItem: FC<UnstoredTourStepItemProps> = ({ step, collectionActions }) => {
    const disabled = useFieldDisabled();

    return (
        <Paper
            withBorder
            p="sm"
            mb="sm"
        >
            <Group
                justify="space-between"
                wrap="nowrap"
                gap="sm"
            >
                <Box style={{ minWidth: 0 }}>
                    <Text fw={600}>{defaultTourStepTitle(step) || tourStepKey(step)}</Text>
                    <Code>{tourStepKey(step)}</Code>
                </Box>
                <Group
                    gap="xs"
                    wrap="nowrap"
                >
                    <Switch
                        label="Show in tour"
                        labelPosition="left"
                        size="xs"
                        checked
                        disabled={disabled}
                        onChange={event =>
                            collectionActions.add(
                                defaultStoredStep(step, event.currentTarget.checked),
                                changeOptions
                            )
                        }
                    />
                    <Button
                        size="xs"
                        variant="light"
                        disabled={disabled}
                        onClick={() =>
                            collectionActions.add(defaultStoredStep(step, true), changeOptions)
                        }
                    >
                        Add text
                    </Button>
                </Group>
            </Group>
            <Text
                size="sm"
                c="dimmed"
                mt="xs"
            >
                {defaultPreview(step)}
            </Text>
        </Paper>
    );
};

/** Supplies the actions used to remove a row. */
type OrphanedTourStepItemProps = {
    collectionActions: TourStepActions;
};

/**
 * Renders a stored row whose step the page tour no longer has.
 *
 * Preconditions: Rendered by the orphaned rows collection, which scopes property paths to the row.
 * Postconditions: Remove deletes the row from the draft; saving removes it.
 */
const OrphanedTourStepItem: FC<OrphanedTourStepItemProps> = ({ collectionActions }) => {
    const disabled = useFieldDisabled();
    const index = useArrayFieldItemIndex();
    const key = useRowValue<string>("stepKey");
    const title = useRowValue<string>("title");

    return (
        <Group
            justify="space-between"
            mb="xs"
        >
            <Box>
                <Text>{title || key}</Text>
                <Code>{key}</Code>
            </Box>
            <Button
                size="xs"
                variant="default"
                disabled={disabled}
                onClick={() => collectionActions.delete(index, changeOptions)}
            >
                Remove
            </Button>
        </Group>
    );
};

/**
 * Selects a value from the draft's stored rows, re-rendering the caller only when it changes.
 *
 * Postconditions: The selection keeps its identity across renders, so the subscription is never
 * replaced; the latest selector is used when the rows next change.
 */
const useStoredStepsSelection = <TSelected,>(selector: (rows: StoredTourStep[]) => TSelected) => {
    const select = useStableCallback((snapshot: { value?: StoredTourStep[] | null }) =>
        selector(snapshot.value ?? [])
    );
    return useFieldSelector<
        HelpTopic,
        TMDValidationError,
        TMDFormSubmitMetadata,
        StoredTourStep[],
        LookupOption,
        TSelected
    >(
        select,
        { value: true },
        { scope: Scope.PropertyPath, propertyPath: "tourSteps", prependParentPropertyPath: false }
    );
};

/** Supplies one chapter's hard-coded steps. */
type ChapterDescriptionProps = {
    steps: HelpTourStep[];
};

/**
 * Renders a chapter's step count with how many of its steps are changed or hidden.
 *
 * Postconditions: Subscribes to the rows itself, so a count change renders only this text.
 */
const ChapterDescription: FC<ChapterDescriptionProps> = ({ steps }) => {
    const describe = useStableCallback((rows: StoredTourStep[]) => {
        const stepsByKey = new Map(steps.map(step => [tourStepKey(step), step]));
        let changed = 0;
        let hidden = 0;
        for (const row of rows) {
            const step = stepsByKey.get(row.stepKey);
            if (step) {
                changed += isTourStepChanged(step, row) ? 1 : 0;
                hidden += row.active === false ? 1 : 0;
            }
        }
        return [
            `${steps.length} ${steps.length === 1 ? "step" : "steps"}`,
            changed ? `${changed} changed` : undefined,
            hidden ? `${hidden} hidden` : undefined
        ]
            .filter(Boolean)
            .join(" · ");
    });
    const description = useStoredStepsSelection(describe);

    return (
        <Text
            size="sm"
            c="dimmed"
        >
            {description}
        </Text>
    );
};

/** Supplies one chapter's hard-coded steps and the actions used to add a row. */
type UnstoredChapterStepsProps = {
    steps: HelpTourStep[];
    collectionActions: TourStepActions;
};

/**
 * Renders the chapter's steps that have no stored row.
 *
 * Postconditions: Subscribes to which keys are stored, so editing a row's text renders nothing here.
 */
const UnstoredChapterSteps: FC<UnstoredChapterStepsProps> = ({ steps, collectionActions }) => {
    const selectStoredKeys = useStableCallback((rows: StoredTourStep[]) =>
        rows
            .map(row => row.stepKey)
            .sort()
            .join("\n")
    );
    const storedKeys = useStoredStepsSelection(selectStoredKeys);
    const unstored = useMemo(() => {
        const stored = new Set(storedKeys.split("\n"));
        return steps.filter(step => !stored.has(tourStepKey(step)));
    }, [steps, storedKeys]);

    return unstored.map(step => (
        <UnstoredTourStepItem
            key={tourStepKey(step)}
            step={step}
            collectionActions={collectionActions}
        />
    ));
};

/** Supplies the page tour's steps by key and the actions used to remove a row. */
type OrphanedTourStepsProps = {
    stepsByKey: Map<string, HelpTourStep>;
    collectionActions: TourStepActions;
};

/**
 * Lists stored rows whose step the page tour no longer has, so they can be removed.
 *
 * Postconditions: Subscribes to the number of such rows; renders nothing when there are none.
 */
const OrphanedTourSteps: FC<OrphanedTourStepsProps> = ({ stepsByKey, collectionActions }) => {
    const isOrphaned = useStableCallback(
        (row: StoredTourStep | null | undefined) => !!row && !stepsByKey.has(row.stepKey)
    );
    const countOrphaned = useStableCallback(
        (rows: StoredTourStep[]) => rows.filter(isOrphaned).length
    );
    const orphaned = useStoredStepsSelection(countOrphaned);

    if (!orphaned) {
        return null;
    }

    return (
        <Paper
            withBorder
            p="sm"
        >
            <Title order={5}>No longer in the tour</Title>
            <Text
                size="sm"
                c="dimmed"
                mb="xs"
            >
                Text stored for steps the page tour no longer has. It is not shown anywhere.
            </Text>
            <Collection
                propertyPath="tourSteps"
                filter={isOrphaned}
                itemKeySelector={storedStepKey}
            >
                <OrphanedTourStepItem collectionActions={collectionActions} />
            </Collection>
        </Paper>
    );
};

/** Supplies the help route whose hard-coded tour steps are edited. */
export type HelpTourStepsFieldProps = {
    routeKey: string;
};

/**
 * Hosts the text of the page tour's hard-coded steps, grouped by chapter.
 *
 * Preconditions: Mounted within the help topic form, whose root owns the tourSteps array.
 * Postconditions: The field itself subscribes to no form state. Each chapter renders its stored
 * rows through a collection, so typing in a step renders only that step; chapter counts, steps
 * without a row and orphaned rows are rendered by components that subscribe to them alone. Closed
 * chapters are unmounted. Steps without a stored row gain one when given text or hidden. Renders
 * nothing for a page without its own tour.
 */
const HelpTourStepsField: FC<HelpTourStepsFieldProps> = ({ routeKey }) => {
    const steps = helpTours[routeKey];
    const stepsByKey = useMemo(
        () => new Map((steps ?? []).map(step => [tourStepKey(step), step])),
        [steps]
    );
    // Chapters and their collection filters keep their identity across renders.
    const chapters = useMemo(() => {
        const grouped = new Map<string, HelpTourStep[]>();
        for (const step of steps ?? []) {
            const chapter = step.chapter ?? closingChapter;
            grouped.set(chapter, [...(grouped.get(chapter) ?? []), step]);
        }
        return [...grouped].map(([chapter, chapterSteps]) => {
            const keys = new Set(chapterSteps.map(tourStepKey));
            return {
                chapter,
                steps: chapterSteps,
                filter: (row: StoredTourStep | null | undefined) => !!row && keys.has(row.stepKey)
            };
        });
    }, [steps]);
    const collectionActions = useArrayFieldActions<
        HelpTopic,
        TMDValidationError,
        TMDFormSubmitMetadata,
        StoredTourStep,
        LookupOption
    >({ scope: Scope.PropertyPath, propertyPath: "tourSteps", prependParentPropertyPath: false });

    if (!steps?.length) {
        return null;
    }

    return (
        <Stack
            gap="sm"
            p="sm"
        >
            <Box>
                <Title order={3}>Tour steps</Title>
                <Text
                    size="sm"
                    c="dimmed"
                >
                    The page tour&apos;s steps are fixed; their titles and text can be changed here.
                    Hidden steps are left out of the tour.
                </Text>
            </Box>
            <GroupedFieldsProvider groupedFields={false}>
                {/* Closed chapters unmount, so only the open chapters' steps are rendered. */}
                <Accordion
                    multiple
                    variant="separated"
                    keepMounted={false}
                >
                    {chapters.map(({ chapter, steps: chapterSteps, filter }) => (
                        <Accordion.Item
                            key={chapter}
                            value={chapter}
                        >
                            <Accordion.Control>
                                <Group justify="space-between">
                                    <Text fw={600}>{chapter}</Text>
                                    <ChapterDescription steps={chapterSteps} />
                                </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Collection
                                    propertyPath="tourSteps"
                                    filter={filter}
                                    itemKeySelector={storedStepKey}
                                >
                                    <HelpTourStepItem stepsByKey={stepsByKey} />
                                </Collection>
                                <UnstoredChapterSteps
                                    steps={chapterSteps}
                                    collectionActions={collectionActions}
                                />
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>
                <OrphanedTourSteps
                    stepsByKey={stepsByKey}
                    collectionActions={collectionActions}
                />
            </GroupedFieldsProvider>
        </Stack>
    );
};

export default HelpTourStepsField;
